/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from 'src/app/core/components';
import { Component } from '@angular/core';
import { DataServicesApiService, SIPApiService } from '../../../../shared';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LanguageService, LogService, RbacService, UtilsService } from '../../../../../../core/services';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { portRangeValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-prometheus',
    templateUrl: './prometheus.component.html'
})

export class PrometheusComponent extends BaseComponent {
    /**
     * Flag on loading state.
     */
    public isLoading: boolean = true;

    /**
     * Dropdown values.
     */
    public dropdown = {
        ips: []
    };

    /**
     * Prometheus HAProxy Form.
     */
    public prometheusForm: FormGroup;

    /**
     * Submit button disabled state.
     */
    public submitButtonDisabled: boolean = true;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * To store the initial form data.
     * @private
     */
    private initialHaFormData: any = {};

    /**
     * To store the initial form data.
     * @private
     */
    private initialNodeFormData: any = {};

    constructor(
        private dataServicesAipService: DataServicesApiService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private logService: LogService,
        private rbacService: RbacService,
        private sipApiService: SIPApiService,
        private utilsService: UtilsService,
    ) {
        super();

        this.setupFormGroups();
        this.setupFormSubscribers();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Returns the form group as FormGroup.
     * @param group
     */
    public getFormGroup(group: any): FormGroup {
        return this.prometheusForm.get(group) as FormGroup;
    }

    /**
     * Submit our prometheus data.
     */
    public prometheusSubmit(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Push our prometheus object in to an array.
            const submitData = this.setUpPrometheusObject(this.prometheusForm.value);

            this.dataServicesAipService.updateDataServiceByName('prometheus', submitData)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        this.prometheusForm.markAsPristine();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'PrometheusComponent: prometheusSubmit',
                            'Error submitting data',
                            error
                        );
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up our from and set the values if we already have them.
     * @private
     */
    private setupFormGroups(): void {
        this.prometheusForm = this.formBuilder.group({
            // Set up our prometheus HAProxy form.
            haProxyForm: this.formBuilder.group({
                enabled: [false],
                port: [{ value: null, disabled: true }, [portRangeValidation, Validators.required]],
                ipID: [{ value: null, disabled: true }, Validators.required],
            }),
            // Set up our prometheus node form.
            nodeForm: this.formBuilder.group({
                enabled: [false],
                port: [{ value: null, disabled: true }, [portRangeValidation, Validators.required]],
                ipID: [{ value: null, disabled: true }, Validators.required],
            })
        });
    }

    /**
     * Setup form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        this.prometheusForm.get('haProxyForm.enabled').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state): void => {
                    this.enableDisableFields(state, this.prometheusForm.get('haProxyForm'));

                    if (this.prometheusForm.get('haProxyForm').get('enabled').value === false) {
                        this.prometheusForm.get('nodeForm.ipID').setErrors(null);
                        this.prometheusForm.get('nodeForm.port').setErrors(null);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PrometheusComponent: setupFormSubscribers ha.enable', error.message);
                }
            });

        this.prometheusForm.get('nodeForm.enabled').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: state => {
                    this.enableDisableFields(state, this.prometheusForm.get('nodeForm'));

                    if (this.prometheusForm.get('nodeForm').get('enabled').value === false) {
                        this.prometheusForm.get('haProxyForm.ipID').setErrors(null);
                        this.prometheusForm.get('haProxyForm.port').setErrors(null);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PrometheusComponent: setupFormSubscribers node.enable', error.message);
                }
            });

        this.prometheusForm.get('haProxyForm').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: values => {
                    this.utilsService.debounce('haProxyForm', (): void => {
                        if (values.port) {
                            this.prometheusForm.get('haProxyForm.port').markAsDirty();
                            this.prometheusForm.get('haProxyForm.port').updateValueAndValidity({ emitEvent: false });
                        }
                        if (values.port && values.ipID) {
                            this.checkIpPortDuplicates(this.prometheusForm.get('haProxyForm'), values, this.prometheusForm.get('nodeForm'));
                        }
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PrometheusComponent: setupFormSubscribers haForm', error.message);
                }
            });

        this.prometheusForm.get('nodeForm').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: values => {
                    this.utilsService.debounce('nodeForm', (): void => {
                        if (values.port) {
                            this.prometheusForm.get('nodeForm.port').markAsDirty();
                            this.prometheusForm.get('nodeForm.port').updateValueAndValidity({ emitEvent: false });
                        }
                        if (values.port && values.ipID) {
                            this.checkIpPortDuplicates(this.prometheusForm.get('nodeForm'), values, this.prometheusForm.get('haProxyForm'));
                        }
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PrometheusComponent: setupFormSubscribers nodeForm', error.message);
                }
            });

        this.prometheusForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.utilsService.debounce('prometheusForm', (): void => {
                        this.controlFormState(this.prometheusForm.getRawValue());
                        this.submitButtonDisabled = this.prometheusForm.invalid || this.prometheusForm.pristine;
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PrometheusComponent: setupFormSubscribers page', error.message);
                }
            });
    }

    /**
     * Enable and disabled fields when enable is toggled.
     * @param state
     * @param form
     * @private
     */
    private enableDisableFields(state: any, form: AbstractControl): void {
        const portControl: AbstractControl = form.get('port');
        const ipIdControl: AbstractControl = form.get('ipID');

        if (state) {
            portControl.enable();
            ipIdControl.enable();
        } else {
            portControl.disable();
            ipIdControl.disable();
            portControl.setErrors(null);
            ipIdControl.setErrors(null);
        }
    }

    /**
     * Check for duplicate port and IP
     * @private
     */
    private checkIpPortDuplicates(form: any, values: any, otherExporterForm: any): void {
        if (
            otherExporterForm.get('enabled').value === true &&
            values.ipID === otherExporterForm.get('ipID').value &&
            values.port === otherExporterForm.get('port').value &&
            values.port !== '' &&
            values.ipID !== null
        ) {
            form.get('ipID').setErrors({ 'duplicateValue': `${this.languageService.translate('prometheus_error_duplicate')}` });
            form.get('port').setErrors({ 'duplicateValue': `${this.languageService.translate('prometheus_error_duplicate')}` });
            form.get('ipID').markAsTouched();
            form.get('port').markAsTouched();
        } else {
            form.get('ipID').setErrors(null);
            form.get('port').setErrors(null);
            form.get('ipID').markAsDirty();
            form.get('port').markAsDirty();
            otherExporterForm.get('ipID').setErrors(null);
            otherExporterForm.get('port').setErrors(null);
        }
    }

    /**
     * Control the form state.
     * @param values
     * @private
     */
    private controlFormState(values: { haProxyForm: any; nodeForm: any; }): void {
        if (!this.utilsService.hasFormDataChanged(this.initialHaFormData, values.haProxyForm)) {
            this.prometheusForm.get('haProxyForm').markAsPristine();
        } else {
            this.prometheusForm.get('haProxyForm').markAsDirty();
        }

        if (!this.utilsService.hasFormDataChanged(this.initialNodeFormData, values.nodeForm)) {
            this.prometheusForm.get('nodeForm').markAsPristine();
        } else {
            this.prometheusForm.get('nodeForm').markAsDirty();
        }
    }

    /**
     * Set up the page data and add/edit values.
     * @private
     */
    private setUpPageData(): void {
        forkJoin({
            ips: this.sipApiService.getStaticIPs(),
            prometheusConfig: this.dataServicesAipService.getDataServicesByName('prometheus'),
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                dataGroup => {
                    if (dataGroup['ips'].length) {
                        this.dropdown.ips = this.utilsService.convertArrayToDropDown(
                            dataGroup['ips'], 'id', 'ip'
                        );
                    }

                    if (dataGroup['prometheusConfig']) {
                        this.patchForms(dataGroup['prometheusConfig']);
                    }

                    this.isLoading = false;
                }
            );
    }

    /**
     * Patch the values of our current exporters to our forms.
     * @private
     */
    private patchForms(prometheusConfig: any): void {
        if (!prometheusConfig.length) {
            const ipID = this.dropdown.ips.length === 1 ? this.dropdown.ips[0].key : null;
            // Fake an empty object so the form validates correctly on a new box.
            prometheusConfig = [
                { name: 'HAProxy', enabled: false, port: 9090, ipID },
                { name: 'node_exporter', enabled: false, port: 9100, ipID }
            ];
        }

        // Set our objects with the current values
        for (const collector of prometheusConfig) {
            if (collector.name === 'HAProxy') {
                this.prometheusForm.get('haProxyForm').patchValue(collector);
            }
            if (collector.name === 'node_exporter') {
                this.prometheusForm.get('nodeForm').patchValue(collector);
            }
        }

        this.initialHaFormData = this.prometheusForm.getRawValue().haProxyForm;
        this.initialNodeFormData = this.prometheusForm.getRawValue().nodeForm;
    }

    /**
     * Set up our prometheus object with the values from the form.
     * @private
     */
    private setUpPrometheusObject(values: { haProxyForm: any; nodeForm: any; }): any {
        const haProxyForm = values.haProxyForm;
        const nodeForm = values.nodeForm;
        const newConfig = [];

        // Set the prometheus object from the form.
        newConfig.push(PrometheusComponent.formValuesToApiObject('HAProxy', haProxyForm));
        newConfig.push(PrometheusComponent.formValuesToApiObject('node_exporter', nodeForm));

        return newConfig;
    }

    /**
     * Takes form values or an empty object and sets up the data service object.
     * @param name
     * @param form
     * @private
     */
    private static formValuesToApiObject(name: string, form: FormGroup): any {
        const enabled = form && form['enabled'] || false;
        const ipID = form && form['ipID'] || '';
        const port = form && form['port'] || '';

        if (!enabled) {
            return { name: name, enabled };
        } else {
            return { name: name, enabled, ipID, port };
        }
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'prometheus-set-exporters': [{ path: '/data-services/:name', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['prometheus-set-exporters']) {
            this.prometheusForm.disable();
        }
    }
}
